import React from 'react';
import { BrowserRouter, Routes, Route, useParams, Link, Navigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import qs from 'qs';
import './variables.css'; 
import './HomePage.css';
import DiagnosisView from './components/DiagnosisView';
import Header from './components/Header';
import LoginPage from './components/LoginPage';
import { AuthProvider, useAuth } from './AuthContext';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL || 'http://localhost:1337';

// --- Protected Route Component ---
const ProtectedRoute = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return <div className="loading">Verifying authentication...</div>;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return children;
};

// --- Homepage Component ---
const HomePage = () => {
    const [systems, setSystems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedSystem, setExpandedSystem] = useState(null);
    const [expandedChapter, setExpandedChapter] = useState(null);
    const { authFetch } = useAuth();

    useEffect(() => {
        const query = qs.stringify({ 
            populate: [
                'icon',
                'chapters.diagnoses',
                'chapters.chapter_items.diagnoses',
                'chapters.chapter_items.icon'
            ]
        });
        const apiUrl = `${STRAPI_URL}/api/organ-systems?${query}`;

        const fetchData = async () => {
            try {
                const res = await authFetch(apiUrl);
                if (!res.ok) throw new Error('Failed to fetch organ systems.');
                const data = await res.json();
                console.log('Fetched organ systems:', data);
                setSystems(data.data || []);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [authFetch]);

    const toggleSystem = (systemId) => {
        setExpandedSystem(expandedSystem === systemId ? null : systemId);
        setExpandedChapter(null);
    };

    const toggleChapter = (chapterId) => {
        setExpandedChapter(expandedChapter === chapterId ? null : chapterId);
    };

    // Render a single chapter item (heading, section, or link)
    const renderChapterItem = (item) => {
        const itemData = item.attributes || item;
        const itemType = itemData.item_type;
        const iconData = itemData.icon?.data?.attributes || itemData.icon;
        const diagnosesData = itemData.diagnoses?.data || itemData.diagnoses || [];

        return (
            <div key={`ci-${item.id}`} className="chapter-item-group">
                {/* Render the chapter item itself */}
                {itemType === 'heading' ? (
                    <div className="chapter-card">
                        <div className="chapter-card-main">
                            {iconData?.url && (
                                <img
                                    src={iconData.url.startsWith('http') ? iconData.url : `${STRAPI_URL}${iconData.url}`}
                                    alt=""
                                    className="chapter-card-icon"
                                />
                            )}
                            <div className="chapter-card-text-content">
                                <div className="chapter-card-title-row">
                                    <h4 className="chapter-card-title">{itemData.title}</h4>
                                </div>
                                {itemData.description && (
                                    <p className="chapter-card-description">{itemData.description}</p>
                                )}
                                {diagnosesData.length > 0 && (
                                    <div className="chapter-diagnoses-container">
                                        {diagnosesData.map((diagnosis) => {
                                            const diagnosisData = diagnosis.attributes || diagnosis;
                                            return (
                                                <Link
                                                    key={diagnosis.id}
                                                    to={`/view?diagnosisId=${diagnosisData.snomed_id || diagnosis.id}`}
                                                    className="chapter-diagnosis-item"
                                                >
                                                    <span className="diagnosis-bullet">•</span>
                                                    <span className="diagnosis-title">
                                                        {diagnosisData.title || 'Unnamed Diagnosis'}
                                                    </span>
                                                </Link>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : (
                    <>
                        {itemType === 'section' && (
                            <div className="chapter-section-item">
                                {itemData.title}
                            </div>
                        )}
                        {itemType === 'link' && (() => {
                            let linkDest = itemData.url || '#';
                            const isExternal = linkDest.startsWith('http');
                            if (!isExternal && !linkDest.startsWith('/') && linkDest !== '#') {
                                linkDest = `/view?diagnosisId=${linkDest}`;
                            }

                            const LinkContent = (
                                <>
                                    {iconData?.url && (
                                        <img
                                            src={iconData.url.startsWith('http') ? iconData.url : `${STRAPI_URL}${iconData.url}`}
                                            alt=""
                                            className="chapter-link-icon"
                                        />
                                    )}
                                    <span className="chapter-link-title">{itemData.title}</span>
                                </>
                            );

                            return isExternal ? (
                                <a
                                    href={linkDest}
                                    className="chapter-link-item"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {LinkContent}
                                </a>
                            ) : (
                                <Link to={linkDest} className="chapter-link-item">
                                    {LinkContent}
                                </Link>
                            );
                        })()}

                        {/* Render diagnoses under this chapter item */}
                        {diagnosesData.length > 0 && (
                            <div className="chapter-item-diagnoses">
                                {diagnosesData.map((diagnosis) => {
                                    const diagnosisData = diagnosis.attributes || diagnosis;
                                    return (
                                        <Link
                                            key={diagnosis.id}
                                            to={`/view?diagnosisId=${diagnosisData.snomed_id || diagnosis.id}`}
                                            className="diagnosis-item"
                                        >
                                            <span className="diagnosis-bullet">•</span>
                                            <span className="diagnosis-title">
                                                {diagnosisData.title || 'Unnamed Diagnosis'}
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    if (isLoading) return <div className="loading">Loading Organ Systems...</div>;
    if (error) return <div className="error-message">Error: {error}</div>;

    return (
        <div className="evidence-container">
            <Header />

            <main className="evidence-main">
                <div className="organ-systems-list">
                    {systems.length === 0 ? (
                        <p className="no-data">No organ systems found. Please add content in Strapi.</p>
                    ) : (
                        systems.map((system) => {
                            const systemData = system.attributes || system;
                            const iconData = systemData.icon?.data?.attributes || systemData.icon;
                            const chaptersRaw = systemData.chapters?.data || systemData.chapters || [];
                            // Sort chapters client-side by title
                            const chaptersData = [...chaptersRaw].sort((a, b) => {
                                const aD = a.attributes || a;
                                const bD = b.attributes || b;
                                return (aD.display_title || aD.title || '').localeCompare(bD.display_title || bD.title || '');
                            });

                            return (
                                <div key={system.id} className="organ-system-item">
                                    <div 
                                        className="organ-system-header"
                                        onClick={() => toggleSystem(system.id)}
                                    >
                                        <div className="organ-system-icon">
                                            {iconData?.url ? (
                                                <img 
                                                    src={iconData.url.startsWith('http') ? iconData.url : `${STRAPI_URL}${iconData.url}`}
                                                    alt={systemData.name || 'System icon'}
                                                    className="icon-image"
                                                />
                                            ) : (
                                                <div className="icon-placeholder" />
                                            )}
                                        </div>
                                        <h3 className="organ-system-title">
                                            {systemData.name || 'Unnamed System'}
                                        </h3>
                                        <span className="toggle-icon">
                                            {expandedSystem === system.id ? '−' : '+'}
                                        </span>
                                    </div>

                                    {expandedSystem === system.id && chaptersData.length > 0 && (
                                        <div className="chapters-list">
                                            {chaptersData.map((chapter) => {
                                                const chapterData = chapter.attributes || chapter;
                                                // Sort chapter items by order client-side
                                                const chapterItemsRaw = chapterData.chapter_items?.data || chapterData.chapter_items || [];
                                                const chapterItems = [...chapterItemsRaw].sort((a, b) => {
                                                    const aO = (a.attributes || a).order || 0;
                                                    const bO = (b.attributes || b).order || 0;
                                                    return aO - bO;
                                                });
                                                const allChapterDiagnoses = chapterData.diagnoses?.data || chapterData.diagnoses || [];

                                                // Find diagnoses NOT assigned to any chapter item (unassigned)
                                                const assignedDiagIds = new Set();
                                                for (const ci of chapterItems) {
                                                    const ciData = ci.attributes || ci;
                                                    const ciDiags = ciData.diagnoses?.data || ciData.diagnoses || [];
                                                    for (const d of ciDiags) {
                                                        assignedDiagIds.add(d.id);
                                                    }
                                                }
                                                const unassignedDiagnoses = allChapterDiagnoses.filter(
                                                    d => !assignedDiagIds.has(d.id)
                                                );

                                                const hasContent = chapterItems.length > 0 || unassignedDiagnoses.length > 0;

                                                return (
                                                    <div key={chapter.id} className="chapter-item">
                                                        <div 
                                                            className="chapter-header"
                                                            onClick={() => toggleChapter(chapter.id)}
                                                        >
                                                            <span className="chapter-title">
                                                                {chapterData.display_title || chapterData.title || 'Unnamed Chapter'}
                                                            </span>
                                                            <span className="toggle-icon-small">
                                                                {expandedChapter === chapter.id ? '−' : '+'}
                                                            </span>
                                                        </div>

                                                        {expandedChapter === chapter.id && hasContent && (
                                                            <div className="chapter-content-list">
                                                                {/* Render chapter items (headings, sections, links) with their diagnoses */}
                                                                {chapterItems.map(item => renderChapterItem(item))}

                                                                {/* Render unassigned diagnoses at the bottom */}
                                                                {unassignedDiagnoses.length > 0 && (
                                                                    <div className="chapter-item-diagnoses">
                                                                        {unassignedDiagnoses.map((diagnosis) => {
                                                                            const diagnosisData = diagnosis.attributes || diagnosis;
                                                                            return (
                                                                                <Link
                                                                                    key={diagnosis.id}
                                                                                    to={`/view?diagnosisId=${diagnosisData.snomed_id || diagnosis.id}`}
                                                                                    className="diagnosis-item"
                                                                                >
                                                                                    <span className="diagnosis-bullet">•</span>
                                                                                    <span className="diagnosis-title">
                                                                                        {diagnosisData.title || 'Unnamed Diagnosis'}
                                                                                    </span>
                                                                                </Link>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })
                    )}
                </div>
            </main>
        </div>
    );
};

// --- System Page Component ---
const SystemPage = () => {
    const { systemId } = useParams();
    const [system, setSystem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openChapter, setOpenChapter] = useState(null);
    const { authFetch } = useAuth();

    useEffect(() => {
        const query = qs.stringify({
            populate: [
                'icon',
                'chapters.diagnoses',
                'chapters.chapter_items.diagnoses',
                'chapters.chapter_items.icon'
            ]
        });
        const apiUrl = `${STRAPI_URL}/api/organ-systems/${systemId}?${query}`;

        const fetchData = async () => {
            try {
                const res = await authFetch(apiUrl);
                if (!res.ok) throw new Error('Failed to fetch system data.');
                const data = await res.json();
                setSystem(data.data);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [systemId, authFetch]);

    const toggleChapter = (chapterId) => {
        setOpenChapter(openChapter === chapterId ? null : chapterId);
    };

    if (isLoading) return <div className="text-center p-10">Loading Chapters...</div>;
    if (error)     return <div className="text-center p-10 text-red-600 font-bold">Error: {error}</div>;
    if (!system)   return null;

    const systemData = system.attributes || system;
    const chaptersRaw = systemData.chapters?.data || systemData.chapters || [];
    const chaptersData = [...chaptersRaw].sort((a, b) => {
        const aD = a.attributes || a;
        const bD = b.attributes || b;
        return (aD.display_title || aD.title || '').localeCompare(bD.display_title || bD.title || '');
    });

    return (
        <div className="max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-3xl font-semibold mb-6 border-b pb-4">{systemData.name}</h2>
            <div className="space-y-2">
                {chaptersData.map((chapter) => {
                    const chapterData = chapter.attributes || chapter;
                    const chapterItemsRaw = chapterData.chapter_items?.data || chapterData.chapter_items || [];
                    const chapterItems = [...chapterItemsRaw].sort((a, b) => {
                        const aO = (a.attributes || a).order || 0;
                        const bO = (b.attributes || b).order || 0;
                        return aO - bO;
                    });
                    const allChapterDiagnoses = chapterData.diagnoses?.data || chapterData.diagnoses || [];

                    // Find unassigned diagnoses
                    const assignedDiagIds = new Set();
                    for (const ci of chapterItems) {
                        const ciData = ci.attributes || ci;
                        const ciDiags = ciData.diagnoses?.data || ciData.diagnoses || [];
                        for (const d of ciDiags) {
                            assignedDiagIds.add(d.id);
                        }
                    }
                    const unassignedDiagnoses = allChapterDiagnoses.filter(
                        d => !assignedDiagIds.has(d.id)
                    );

                    const hasContent = chapterItems.length > 0 || unassignedDiagnoses.length > 0;

                    return (
                    <div key={chapter.id} className="border rounded-md">
                        <button
                            onClick={() => toggleChapter(chapter.id)}
                            className="w-full flex justify-between items-center p-4 text-left hover:bg-gray-50"
                        >
                            <span className="text-lg font-medium">
                                {chapterData.display_title || chapterData.title}
                            </span>
                            <span>{openChapter === chapter.id ? '−' : '+'}</span>
                        </button>
                        {openChapter === chapter.id && hasContent && (
                            <div className="p-4 border-t bg-gray-50">
                                {/* Render chapter items with their diagnoses */}
                                {chapterItems.map(item => {
                                    const itemData = item.attributes || item;
                                    const itemType = itemData.item_type;
                                    const iconData = itemData.icon?.data?.attributes || itemData.icon;
                                    const itemDiagnoses = itemData.diagnoses?.data || itemData.diagnoses || [];

                                    return (
                                        <div key={`ci-${item.id}`} className="chapter-item-group">
                                            {itemType === 'heading' ? (
                                                <div className="chapter-card">
                                                    <div className="chapter-card-main">
                                                        {iconData?.url && (
                                                            <img
                                                                src={iconData.url.startsWith('http') ? iconData.url : `${STRAPI_URL}${iconData.url}`}
                                                                alt=""
                                                                className="chapter-card-icon"
                                                            />
                                                        )}
                                                        <div className="chapter-card-text-content">
                                                            <div className="chapter-card-title-row">
                                                                <h4 className="chapter-card-title">{itemData.title}</h4>
                                                            </div>
                                                            {itemData.description && (
                                                                <p className="chapter-card-description">{itemData.description}</p>
                                                            )}
                                                            {itemDiagnoses.length > 0 && (
                                                                <div className="chapter-diagnoses-container">
                                                                    {itemDiagnoses.map((d) => {
                                                                        const dData = d.attributes || d;
                                                                        return (
                                                                            <Link
                                                                                key={d.id}
                                                                                to={`/view?diagnosisId=${dData.snomed_id || d.id}`}
                                                                                className="chapter-diagnosis-item"
                                                                            >
                                                                                <span className="diagnosis-bullet">•</span>
                                                                                <span className="diagnosis-title">
                                                                                    {dData.title}
                                                                                </span>
                                                                            </Link>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    {itemType === 'section' && (
                                                        <div className="chapter-section-item" style={{paddingLeft: '1.5rem'}}>
                                                            {itemData.title}
                                                        </div>
                                                    )}
                                                    {itemType === 'link' && (() => {
                                                        let linkDest = itemData.url || '#';
                                                        const isExternal = linkDest.startsWith('http');
                                                        if (!isExternal && !linkDest.startsWith('/') && linkDest !== '#') {
                                                            linkDest = `/view?diagnosisId=${linkDest}`;
                                                        }

                                                        const LinkContent = (
                                                            <>
                                                                {iconData?.url && (
                                                                    <img
                                                                        src={iconData.url.startsWith('http') ? iconData.url : `${STRAPI_URL}${iconData.url}`}
                                                                        alt=""
                                                                        className="chapter-link-icon"
                                                                    />
                                                                )}
                                                                <span className="chapter-link-title">{itemData.title}</span>
                                                            </>
                                                        );

                                                        return isExternal ? (
                                                            <a
                                                                href={linkDest}
                                                                className="chapter-link-item" style={{paddingLeft: '1.5rem'}}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {LinkContent}
                                                            </a>
                                                        ) : (
                                                            <Link to={linkDest} className="chapter-link-item" style={{paddingLeft: '1.5rem'}}>
                                                                {LinkContent}
                                                            </Link>
                                                        );
                                                    })()}
                                                    {itemDiagnoses.length > 0 && (
                                                        <div style={{paddingLeft: '1rem'}}>
                                                            {itemDiagnoses.map(d => {
                                                                const dData = d.attributes || d;
                                                                return (
                                                                    <Link
                                                                        key={d.id}
                                                                        to={`/view?diagnosisId=${dData.snomed_id || d.id}`}
                                                                        className="block p-2 text-blue-600 hover:underline"
                                                                    >
                                                                        {dData.title}
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    );
                                })}

                                {/* Unassigned diagnoses */}
                                {unassignedDiagnoses.map(d => {
                                    const dData = d.attributes || d;
                                    return (
                                        <Link
                                            key={d.id}
                                            to={`/view?diagnosisId=${dData.snomed_id || d.id}`}
                                            className="block p-2 text-blue-600 hover:underline"
                                        >
                                            {dData.title}
                                        </Link>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>
        </div>
    );
}; // <-- Added the missing closing bracket here

// --- Simple Rich Text Renderer ---
// eslint-disable-next-line no-unused-vars
const RichTextRenderer = ({ content }) => {
    if (!content) return null;
    
    try {
        const blocks = typeof content === 'string' ? JSON.parse(content) : content;
        
        const renderBlock = (block, index) => {
            switch (block.type) {
                case 'paragraph':
                    return (
                        <p key={index} className="mb-4">
                            {block.children.map((child, i) => {
                                if (child.bold) return <strong key={i}>{child.text}</strong>;
                                if (child.underline) return <u key={i}>{child.text}</u>;
                                return child.text || '';
                            })}
                        </p>
                    );
                case 'list':
                    const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
                    const listClass = block.format === 'ordered' ? 'list-decimal' : 'list-disc';
                    const indent = block.indentLevel ? block.indentLevel * 8 : 0;
                    return (
                        <ListTag key={index} className={`${listClass} ml-6 mb-4`} style={{marginLeft: `${indent}px`}}>
                            {block.children.map((item, i) => (
                                <li key={i} className="mb-2">
                                    {item.children[0]?.text || ''}
                                </li>
                            ))}
                        </ListTag>
                    );
                case 'list-item':
                    return null; // Handled by list
                default:
                    return null;
            }
        };
        
        return <div>{blocks.map((block, index) => renderBlock(block, index))}</div>;
    } catch (error) {
        console.error('Error parsing rich text:', error);
        return <div>Error rendering content</div>;
    }
};

// --- Main App Component with Routing ---
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
          <Route path="/system/:systemId" element={<ProtectedRoute><SystemPage /></ProtectedRoute>} />
          <Route path="/view" element={<ProtectedRoute><DiagnosisView /></ProtectedRoute>} />
          <Route path="/login" element={<LoginPage />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}